// Local-only progress storage — the browser counterpart of the Android build's
// `data/ProgressStore.kt`, which used SharedPreferences.
//
// Nothing is uploaded anywhere; the learner's history stays in this browser.
// Because localStorage is wiped whenever the user clears site data, the
// Progress screen also offers JSON export/import — the one durability
// regression against the phone build, and its answer.
import type { Correction } from './tutor/grammarChecker';
import { EMPTY_PROGRESS, type ProgressSnapshot, type SavedCorrection } from './types';

const K_MESSAGES = 'sadie.messages';
const K_CLEAN = 'sadie.clean';
const K_CORRECTIONS = 'sadie.corrections';
const K_SESSIONS = 'sadie.sessions';
const K_STREAK = 'sadie.streak';
const K_LAST_ACTIVE = 'sadie.last_active';
const K_RULE_COUNTS = 'sadie.rule_counts';
const K_RECENT = 'sadie.recent';
const K_PRACTICE_COUNT = 'sadie.practice_count';
const K_PRACTICE_TOTAL = 'sadie.practice_total';
const K_PRACTICE_BEST = 'sadie.practice_best';
const K_CORRECTIONS_ON = 'sadie.corrections_enabled';
const K_VOICE_ON = 'sadie.voice_replies';

const MAX_RECENT = 40;

/** Every key the store owns, in export order. */
const ALL_KEYS = [
  K_MESSAGES, K_CLEAN, K_CORRECTIONS, K_SESSIONS, K_STREAK, K_LAST_ACTIVE,
  K_RULE_COUNTS, K_RECENT, K_PRACTICE_COUNT, K_PRACTICE_TOTAL, K_PRACTICE_BEST,
  K_CORRECTIONS_ON, K_VOICE_ON,
] as const;

/** localStorage throws in private-mode Safari and when storage is disabled. */
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    console.warn(`[storage] read failed for ${key}`, e);
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Most likely QuotaExceededError. The learner's history is small, so this
    // is unlikely — but swallowing it silently would hide a real data loss.
    console.error(`[storage] write failed for ${key}`, e);
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    console.warn(`[storage] remove failed for ${key}`, e);
  }
}

function readInt(key: string): number {
  const raw = read(key);
  if (raw === null) return 0;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : 0;
}

function readJson<T>(key: string, fallback: T): T {
  const raw = read(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    console.warn(`[storage] malformed JSON at ${key}`, e);
    return fallback;
  }
}

// ------------------------------------------------------------------ snapshot

export function snapshot(): ProgressSnapshot {
  const ruleCounts = readJson<Record<string, number>>(K_RULE_COUNTS, {});
  const topMistakes = Object.entries(ruleCounts)
    .map(([rule, count]) => ({ rule, label: ruleLabel(rule), count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);

  const attempts = readInt(K_PRACTICE_COUNT);
  const totalMessages = readInt(K_MESSAGES);
  const cleanMessages = readInt(K_CLEAN);

  return {
    totalMessages,
    cleanMessages,
    totalCorrections: readInt(K_CORRECTIONS),
    sessions: readInt(K_SESSIONS),
    streakDays: readInt(K_STREAK),
    practiceAttempts: attempts,
    practiceBest: readInt(K_PRACTICE_BEST),
    practiceAverage: attempts === 0 ? 0 : Math.trunc(readInt(K_PRACTICE_TOTAL) / attempts),
    accuracy: totalMessages === 0 ? 0 : Math.trunc((cleanMessages * 100) / totalMessages),
    topMistakes,
    recentCorrections: readJson<SavedCorrection[]>(K_RECENT, []),
  };
}

/** Records one learner message and any corrections it triggered. */
export function recordMessage(corrections: Correction[]): void {
  touchStreak();
  write(K_MESSAGES, String(readInt(K_MESSAGES) + 1));
  if (corrections.length === 0) write(K_CLEAN, String(readInt(K_CLEAN) + 1));
  write(K_CORRECTIONS, String(readInt(K_CORRECTIONS) + corrections.length));

  if (corrections.length === 0) return;

  const counts = readJson<Record<string, number>>(K_RULE_COUNTS, {});
  for (const c of corrections) counts[c.rule] = (counts[c.rule] ?? 0) + 1;
  write(K_RULE_COUNTS, JSON.stringify(counts));

  const recent: SavedCorrection[] = corrections.map((c) => ({
    rule: c.rule,
    original: c.original,
    corrected: c.corrected,
    explanation: c.explanation,
  }));
  const existing = readJson<SavedCorrection[]>(K_RECENT, []);
  write(K_RECENT, JSON.stringify([...recent, ...existing].slice(0, MAX_RECENT)));
}

export function recordSessionStarted(): void {
  touchStreak();
  write(K_SESSIONS, String(readInt(K_SESSIONS) + 1));
}

export function recordPracticeScore(value: number): void {
  touchStreak();
  write(K_PRACTICE_COUNT, String(readInt(K_PRACTICE_COUNT) + 1));
  write(K_PRACTICE_TOTAL, String(readInt(K_PRACTICE_TOTAL) + value));
  write(K_PRACTICE_BEST, String(Math.max(readInt(K_PRACTICE_BEST), value)));
}

// ------------------------------------------------------------------ settings

/** Whether the tutor shows grammar corrections, or just keeps talking. */
export function loadCorrectionsEnabled(): boolean {
  return read(K_CORRECTIONS_ON) !== 'false';
}

export function saveCorrectionsEnabled(value: boolean): void {
  write(K_CORRECTIONS_ON, String(value));
}

/** Whether the tutor's replies are spoken aloud. */
export function loadVoiceReplies(): boolean {
  return read(K_VOICE_ON) !== 'false';
}

export function saveVoiceReplies(value: boolean): void {
  write(K_VOICE_ON, String(value));
}

/** Wipes the learner's history but keeps their settings. */
export function clearProgress(): void {
  for (const key of ALL_KEYS) {
    if (key === K_CORRECTIONS_ON || key === K_VOICE_ON) continue;
    remove(key);
  }
}

// ------------------------------------------------------------ export / import

interface Backup {
  app: 'sadie';
  version: 1;
  exportedAt: string;
  data: Record<string, string>;
}

export function buildExport(): string {
  const data: Record<string, string> = {};
  for (const key of ALL_KEYS) {
    const value = read(key);
    if (value !== null) data[key] = value;
  }
  const backup: Backup = {
    app: 'sadie',
    version: 1,
    exportedAt: new Date().toISOString(),
    data,
  };
  return JSON.stringify(backup, null, 2);
}

export function downloadExport(): void {
  const blob = new Blob([buildExport()], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ai-tutor-progress-${todayIso()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Restores a backup produced by `buildExport`. Returns the number of keys
 * restored, or throws when the file isn't a Sadie backup.
 */
export function applyImport(text: string): number {
  const parsed: unknown = JSON.parse(text);
  if (
    typeof parsed !== 'object' || parsed === null ||
    (parsed as Backup).app !== 'sadie' ||
    typeof (parsed as Backup).data !== 'object' || (parsed as Backup).data === null
  ) {
    throw new Error('That file is not an AI tutor progress export.');
  }
  const data = (parsed as Backup).data;
  let restored = 0;
  for (const key of ALL_KEYS) {
    const value = data[key];
    if (typeof value === 'string') {
      write(key, value);
      restored++;
    }
  }
  return restored;
}

// -------------------------------------------------------------------- streak

/** Today as `YYYY-MM-DD` in the browser's own timezone. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function isoPlusOneDay(iso: string): string | null {
  const parts = iso.split('-').map((p) => Number.parseInt(p, 10));
  if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
  const [y, m, d] = parts as [number, number, number];
  const date = new Date(y, m - 1, d + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Bumps the day streak: +1 for a consecutive day, reset after a gap. */
function touchStreak(): void {
  const today = todayIso();
  const last = read(K_LAST_ACTIVE);
  let streak: number;
  if (last === null) {
    streak = 1;
  } else if (last === today) {
    return;
  } else if (isoPlusOneDay(last) === today) {
    streak = readInt(K_STREAK) + 1;
  } else {
    streak = 1;
  }
  write(K_LAST_ACTIVE, today);
  write(K_STREAK, String(streak));
}

// --------------------------------------------------------------- rule labels

/** Human-readable name for a rule id, used in the mistake breakdown. */
export function ruleLabel(rule: string): string {
  switch (rule) {
    case 'contraction_apostrophe':
      return 'Apostrophes';
    case 'irregular_past':
      return 'Irregular past tense';
    case 'age_with_be':
      return 'Talking about age';
    case 'be_agreement':
      return 'am / is / are';
    case 'stative_no_be':
      return 'Extra "be"';
    case 'progressive_ing':
      return 'Missing "-ing"';
    case 'aux_base_form':
      return 'After do / did';
    case 'modal_base_form':
      return 'After modals';
    case 'subject_dont':
    case 'subject_doesnt':
      return "don't / doesn't";
    case 'third_person_s':
      return 'Third-person "-s"';
    case 'plural_no_s':
      return 'Extra verb "-s"';
    case 'past_tense_needed':
      return 'Past tense';
    case 'infinitive_to':
      return 'Missing "to"';
    case 'gerund_after_verb':
      return 'Verb + "-ing"';
    case 'double_comparative':
      return 'Comparatives';
    case 'double_negative':
      return 'Double negatives';
    case 'article_a_an':
      return 'a / an';
    case 'missing_article_job':
      return 'Missing article';
    case 'uncountable_plural':
      return 'Uncountable nouns';
    case 'much_many':
      return 'much / many';
    case 'there_are':
      return 'there is / there are';
    case 'day_preposition':
    case 'month_preposition':
      return 'Time prepositions';
    case 'adverb_well':
      return 'good / well';
    case 'question_word_order':
      return 'Question word order';
    case 'every_day':
      return 'every day / everyday';
    default:
      return rule.startsWith('phrase_') ? 'Set phrases' : 'Other';
  }
}

export { EMPTY_PROGRESS };
